// Wie das CRM Zeiten, Mengen und Stände für Menschen schreibt – reine
// Logik, ohne React, an einer Stelle. Ähnliche Helfer hat die Doku-App
// (einsicht.ts, memo.ts, gemeinsam.tsx); die Apps teilen keinen Code
// untereinander, nur über packages/ – wandern sie einmal nach
// oberflaeche, dann gemeinsam.

import type { CrmAufnahme, PruefHinweis, PruefZeile } from "@happy-energy/kern";

/** Wann, für Menschen: „heute 10:42“, „gestern 16:05“, sonst „12. Okt.“ (aus einem anderen Jahr mit Jahr). */
export function wannZeigen(iso: string, jetzt = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const uhrzeit = d.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" });
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const tage = Math.round((tag(jetzt) - tag(d)) / 86_400_000);
  if (tage === 0) return `heute ${uhrzeit}`;
  if (tage === 1) return `gestern ${uhrzeit}`;
  return d.toLocaleDateString("de-AT", { day: "numeric", month: "short", ...(d.getFullYear() !== jetzt.getFullYear() ? { year: "numeric" } : {}) });
}

/** Ein Tag mit Uhrzeit, ausgeschrieben: „Mittwoch, 21. Oktober, 10:42“. */
export function zeitpunktZeigen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.toLocaleDateString("de-AT", { weekday: "long", day: "numeric", month: "long" })}, ${d.toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })}`;
}

/** Nur der Tag, kurz: „21. Okt.“ – für „Nachtrag vom …“. */
export function tagZeigen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-AT", { day: "numeric", month: "short" });
}

/**
 * Ein Kalendertag „JJJJ-MM-TT“ (Montagetermin): „Di, 4. Nov.“ – gerechnet
 * als Tag, ohne Zeitzone, damit kein Tag nach vorne oder hinten rutscht.
 */
export function kalendertagZeigen(tag: string): string {
  const [j, m, t] = tag.split("-").map(Number);
  if (!j || !m || !t) return tag;
  const d = new Date(j, m - 1, t);
  return d.toLocaleDateString("de-AT", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

/** „0:08“, „12:30“ */
export function dauerZeigen(sekunden: number): string {
  const s = Math.max(0, Math.round(sekunden));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Eine Zahl mit Komma: 12.5 → „12,5“. */
export function zahlZeigen(zahl: number): string {
  return zahl.toLocaleString("de-AT", { maximumFractionDigits: 2 });
}

/** Die Größe einer Datei: „1,2 MB“, „340 kB“. */
export function groesseZeigen(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toLocaleString("de-AT", { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

// --- Die Buchstaben im runden Zeichen ------------------------------------------------------------
//
// Nur Anzeige, aus dem Namen gerechnet, nirgends gespeichert. Ein Wort ohne
// Buchstaben („4“, „&“, „–“) zählt nicht, und aus einem Wort zählen nur die
// Buchstaben („Huber-Maier“ → H, U …): Seit Gernot am 22.10.2026 im Kopf
// der Freigabe bei „Familie Test 4“ groß eine „4“ sah, steht dort nie eine
// Zahl oder ein Zeichen. Bleibt kein Buchstabe übrig: „?“.

const BUCHSTABE = /\p{L}/u;

/** Die Wörter eines Namens, die mindestens einen Buchstaben haben. */
function wortMitBuchstaben(name: string): string[] {
  return name
    .trim()
    .split(/\s+/)
    .filter((w) => BUCHSTABE.test(w));
}

/** Die Buchstaben eines Wortes, ohne Zeichen dazwischen. */
function buchstabenAus(wort: string): string[] {
  return [...wort].filter((z) => BUCHSTABE.test(z));
}

/**
 * Die Buchstaben im runden Zeichen neben einer Person (Seitenleiste, Fragen,
 * Leute): der erste Buchstabe des ersten und des letzten Wortes („Günther
 * Huber“ → „GH“); ein Wort allein: seine ersten zwei („Büro“ → „BÜ“). Leer:
 * „?“. Seit der Code-Prüfung E3 (Befund 5) EINMAL hier statt dreimal.
 */
export function initialen(name: string): string {
  const woerter = wortMitBuchstaben(name);
  const erstes = woerter[0];
  if (!erstes) return "?";
  const letztes = woerter[woerter.length - 1] ?? erstes;
  const zeichen = woerter.length === 1 ? buchstabenAus(erstes).slice(0, 2) : [buchstabenAus(erstes)[0] ?? "", buchstabenAus(letztes)[0] ?? ""];
  return zeichen.join("").toLocaleUpperCase("de-AT");
}

/** Anrede und Titel – sie zählen fürs Kürzel eines Kunden nicht. */
const ANREDE = /^(frau|herr|familie|fam\.?|dr\.?|ing\.?|mag\.?|di)$/i;

/**
 * Zwei Buchstaben für den runden Kasten eines Kunden: die ersten zwei des
 * Familiennamens, ohne Anrede und Titel – „Frau Maierhofer“ → „MA“,
 * „Familie Test 4“ → „TE“, „Dr. Anna Huber-Maier“ → „HU“. Warum der
 * Familienname und nicht die Anfangsbuchstaben: Kunden heißen im System
 * meist „Familie …“, „Frau …“ oder „Herr …“ – dann ist der Familienname das
 * einzige Wort, das zählt.
 */
export function kuerzel(name: string): string {
  const woerter = wortMitBuchstaben(name);
  const ohneAnrede = woerter.filter((w) => !ANREDE.test(w));
  const wort = ohneAnrede[ohneAnrede.length - 1] ?? woerter[woerter.length - 1];
  if (!wort) return "?";
  return buchstabenAus(wort).slice(0, 2).join("").toLocaleUpperCase("de-AT");
}

/**
 * Heute in Wien „JJJJ-MM-TT“ – so, wie der Server die Tage zählt
 * (server/src/crm/kalender.ts, `wienerTag`): für die kommenden
 * Montagetermine und die Karten im Weg. Intl kennt die Sommerzeit. Seit der
 * Code-Prüfung E3 (Befund 5) EINMAL hier statt zweimal.
 */
export function heuteInWien(jetzt: Date = new Date()): string {
  const teile = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Vienna", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(jetzt).map((t) => [t.type, t.value]),
  );
  return `${teile["year"]}-${teile["month"]}-${teile["day"]}`;
}

/** Die Arten eines Objekts in Worten – „einfamilienhaus“ → „Einfamilienhaus“; andere Arten sind vorbereitet und heißen bis dahin wie ihr Schlüssel. */
const OBJEKT_ARTEN: Record<string, string> = { einfamilienhaus: "Einfamilienhaus" };

export function objektArtName(art: string): string {
  return OBJEKT_ARTEN[art] ?? art;
}

/** Die Adresse einer Datei (Foto, Memo, K2-PDF) am Datei-Weg des Büros – der Browser schickt die Anmeldung mit. */
export function dateiAdresse(pruefsumme: string): string {
  return `/api/crm/datei/${pruefsumme}`;
}

/** „Sonnenweg 12, 8000 Musterdorf“ */
export function adresseZeigen(o: { strasse: string; plz: string; ort: string }): string {
  return `${o.strasse}, ${o.plz} ${o.ort}`;
}

// --- Stände ----------------------------------------------------------------------------

/** Der Schlüssel „neu“ ist der Hinweis des Servers (PruefHinweis) – zu lesen ist „wartet auf Freigabe“. */
export type StandArt = "neu" | "nachtrag" | "bitte-ansehen" | "freigegeben" | "beim-buero" | "nicht-gesendet";

/**
 * Der Stand einer Besichtigung in wenigen Worten. Fürs Büro: „wartet auf
 * Freigabe“, „Nachtrag – zu prüfen“, „bitte ansehen“ (das ist zu tun) oder
 * „freigegeben“. Für den Verkauf, der nur seine eigenen sieht: „beim Büro“
 * oder „freigegeben“ – „bitte ansehen“ ist die Aufgabe des Büros, nicht
 * seine. Ohne Stand (die Anlage hat noch keine gesendete Besichtigung, Karten
 * im Weg): „noch nicht gesendet“.
 */
export function standArt(z: { stand: PruefZeile["stand"] | null; hinweis: PruefHinweis | null }, fuerBuero: boolean): StandArt {
  if (z.stand === null) return "nicht-gesendet";
  if (!fuerBuero) return z.stand === "freigegeben" ? "freigegeben" : "beim-buero";
  if (z.hinweis === "neu") return "neu";
  if (z.hinweis === "nachtrag") return "nachtrag";
  if (z.hinweis === "bitte-ansehen") return "bitte-ansehen";
  return z.stand === "freigegeben" ? "freigegeben" : "neu";
}

/**
 * Was auf dem Chip steht. Seit Gernot am 22.10.2026 fragte „Was heißt neu?“
 * – Manuel soll es auf einen Blick lesen: aus „neu“ wurde „wartet auf
 * Freigabe“, aus „Nachtrag“ „Nachtrag – zu prüfen“.
 */
export const STAND_TEXT: Record<StandArt, string> = {
  neu: "wartet auf Freigabe",
  nachtrag: "Nachtrag – zu prüfen",
  "bitte-ansehen": "bitte ansehen",
  freigegeben: "freigegeben",
  "beim-buero": "beim Büro",
  "nicht-gesendet": "noch nicht gesendet",
};

/** Der eine Satz dazu – erscheint, wenn die Maus auf dem Chip steht. */
export const STAND_SATZ: Record<StandArt, string> = {
  neu: "Gesendet, das Büro hat noch nicht freigegeben.",
  nachtrag: "Der Verkauf hat nach dem Senden etwas nachgereicht – noch nicht freigegeben.",
  "bitte-ansehen": "Nach der Freigabe kam etwas dazu.",
  freigegeben: "Das Büro hat freigegeben, die Materialliste steht.",
  "beim-buero": "Gesendet, das Büro hat noch nicht freigegeben.",
  "nicht-gesendet": "Der Verkauf ist noch dran und hat noch nicht ans Büro gesendet.",
};

// --- „Bitte ansehen“ und „neu“ an der Besichtigung ------------------------------------------------

type Zeiten = Pick<CrmAufnahme, "nachtraege" | "freigegebenAm" | "gesehenAm"> & { anlage: Pick<CrmAufnahme["anlage"], "k2GeaendertAm"> };

/** Was seit der Freigabe neu ist: jünger als die Freigabe UND als das letzte „gesehen“ – dieselbe Regel wie am Server (crm/lesen.ts, hinweisFuer). */
function seitFreigabe(iso: string | null, a: Zeiten): boolean {
  if (iso === null || a.freigegebenAm === null) return false;
  const t = new Date(iso).getTime();
  return t > new Date(a.freigegebenAm).getTime() && (a.gesehenAm === null || t > new Date(a.gesehenAm).getTime());
}

/**
 * Warum „bitte ansehen“ – für das Band oben in der Freigabe: „Nachtrag vom
 * 21. Okt.“, „Dachplanung geändert am 21. Okt.“. Leer, wenn nichts dazukam.
 */
export function bitteAnsehenGruende(a: Zeiten): string[] {
  const gruende = a.nachtraege.filter((n) => seitFreigabe(n.gesendetAm, a)).map((n) => `Nachtrag vom ${tagZeigen(n.gesendetAm!)}`);
  if (seitFreigabe(a.anlage.k2GeaendertAm, a)) gruende.push(`Dachplanung geändert am ${tagZeigen(a.anlage.k2GeaendertAm!)}`);
  return gruende;
}

/**
 * Die Marke an Nachgereichtem: „neu – Nachtrag vom 21. Okt.“, solange das
 * Büro es noch nicht hatte (vor der Freigabe, oder danach und nach dem
 * letzten „gesehen“); sonst nur „Nachtrag vom 21. Okt.“. Ein Nachtrag, der
 * noch nicht ganz im Büro ist: „Nachtrag kommt gerade“.
 */
export function nachtragMarke(nachtragId: string | null, a: Zeiten): { text: string; neu: boolean } | null {
  if (nachtragId === null) return null;
  const n = a.nachtraege.find((x) => x.id === nachtragId);
  if (!n) return { text: "nachgereicht", neu: true };
  if (n.gesendetAm === null) return { text: "Nachtrag kommt gerade", neu: true };
  const neu = a.freigegebenAm === null || seitFreigabe(n.gesendetAm, a);
  return { text: `${neu ? "neu – " : ""}Nachtrag vom ${tagZeigen(n.gesendetAm)}`, neu };
}
